import useForm from 'antd/lib/form/hooks/useForm';
import { useDispatch, useSelector } from 'react-redux';
import { getUpdated, getIsLoading, getError } from '../helpers';
import { RootState } from '../reducers/reducers';
import { AppleAppStoreDefaultSettings, AppStoreType, ReleaseMethod } from '../types';
import * as React from 'react';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import { displayStoreNameForAppStoreType } from '../helpers/displayHelpers';
import { AppSettingsUpdatable } from '../actions/updatable';
import Radio from 'antd/lib/radio';
import { antGrey, runwayColors } from './styles/globalStyles';
import { tryUpdateAppSettings, UpdateAppleAppStoreReleaseDefaults } from '../actions/appSettings';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import { InfoCircleOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { AppComponentProps } from '../typescriptHelpers';
import { selectAppStoreDefaults } from '../selectors/selectors';
import { globalMessage } from '../utils/globalMessage';
import Alert from 'antd/lib/alert';
import Divider from 'antd/lib/divider';
import Switch from 'antd/lib/switch';
import { InitialEncryptionOptions } from '@/types/stepConfig';

interface EditAppleAppReleaseDefaultsProps extends AppComponentProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const EditAppleAppReleaseDefaults: React.FC<EditAppleAppReleaseDefaultsProps> = (
    props: EditAppleAppReleaseDefaultsProps,
) => {
    const [form] = useForm();
    const [requiresAdditionalQuestion, setRequiresAdditionalQuestion] = React.useState<boolean>(false);
    const [usesNonExemptEncryption, setUsesNonExemptEncryption] = React.useState(null);
    const [shouldApplyDefaultEncryption, setShouldApplyDefaultEncryption] = React.useState<boolean>(true);

    const dispatch = useDispatch();

    const appStoreDefaults = useSelector(
        (state: RootState) => selectAppStoreDefaults(state, props.appId) as AppleAppStoreDefaultSettings,
    );

    const releaseSettingsUpdatedSuccess = useSelector((state: RootState) =>
        getUpdated(AppSettingsUpdatable.UpdateAppleAppStoreReleaseDefaults, state),
    );
    const releaseSettingsUpdateLoading = useSelector((state: RootState) =>
        getIsLoading(AppSettingsUpdatable.UpdateAppleAppStoreReleaseDefaults, state),
    );

    const releaseSettingsUpdateError = useSelector((state: RootState) =>
        getError(AppSettingsUpdatable.UpdateAppleAppStoreReleaseDefaults, state),
    );

    const onSaveChanges = () => {
        if (usesNonExemptEncryption === null) {
            globalMessage.error('Please enter export compliance settings for this app.');
            return;
        }

        form.validateFields().then((values) => {
            const request: UpdateAppleAppStoreReleaseDefaults = {
                orgId: props.orgId,
                appId: props.appId,
                resetSummaryRating: false, // hardcoded to false for now
                shouldApplyDefaultEncryption: shouldApplyDefaultEncryption,
                exportComplianceInfoUsesEncryption: usesNonExemptEncryption,
                releaseMethod: values.releaseMethod,
                phasedRelease: values.phasedRelease,
            };

            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateAppleAppStoreReleaseDefaults, request));
        });
    };

    const handleInitialEncryptionSelection = (initialSelection: InitialEncryptionOptions) => {
        switch (initialSelection) {
            case InitialEncryptionOptions.ProprietaryAlgorithms:
                setUsesNonExemptEncryption(true);
                setRequiresAdditionalQuestion(false);
                break;
            case InitialEncryptionOptions.BothAlgorithms:
            case InitialEncryptionOptions.StandardAlgorithms:
                setRequiresAdditionalQuestion(true);
                setUsesNonExemptEncryption(false);
                break;
            case InitialEncryptionOptions.NoAlgorithms:
                setUsesNonExemptEncryption(false);
                setRequiresAdditionalQuestion(false);
                break;
        }
    };

    const handleAdditionalQuestion = (usesNonExemptEncryption: boolean) => {
        setUsesNonExemptEncryption(usesNonExemptEncryption);
    };

    const handleApplyDefaultEncryption = (shouldApply: boolean) => {
        setShouldApplyDefaultEncryption(shouldApply);
    };

    const title = `Edit ${displayStoreNameForAppStoreType(AppStoreType.Apple)} release defaults`;

    React.useEffect(() => {
        if (releaseSettingsUpdatedSuccess) {
            globalMessage.success('App Store release defaults updated!');

            props.onSuccess();
        }
    }, [releaseSettingsUpdatedSuccess]);

    React.useEffect(() => {
        if (releaseSettingsUpdateError) {
            globalMessage.error(releaseSettingsUpdateError);
        }
    }, [releaseSettingsUpdateError]);

    React.useEffect(() => {
        if (props.open) {
            if (appStoreDefaults) {
                form.setFieldsValue({
                    releaseMethod: appStoreDefaults.releaseMethod,
                    phasedRelease: appStoreDefaults.phasedRelease,
                });

                setUsesNonExemptEncryption(appStoreDefaults.exportComplianceInfoUsesEncryption);
                setShouldApplyDefaultEncryption(appStoreDefaults.shouldApplyDefaultEncryption);
            }
        }
    }, [props.open]);

    return (
        <Modal
            title={title}
            onOk={() => form.submit()}
            okButtonProps={{ loading: releaseSettingsUpdateLoading, disabled: usesNonExemptEncryption === null }}
            onCancel={props.onClose}
            destroyOnClose
            open={props.open}
            okText="Update App Store release settings"
            cancelText="Cancel"
            afterClose={() => {
                form.resetFields();

                setRequiresAdditionalQuestion(false);
                setUsesNonExemptEncryption(null);
            }}
            bodyStyle={{ backgroundColor: antGrey.grey3 }}
            width={600}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
                {appStoreDefaults && (
                    <Alert
                        showIcon={false}
                        message={`You previously specified that your app ${
                            appStoreDefaults.exportComplianceInfoUsesEncryption ? 'uses' : 'does not use'
                        } non-exempt encryption. To update this setting, re-enter export compliance information below.`}
                        type="info"
                    />
                )}
                {usesNonExemptEncryption && (
                    <Alert
                        showIcon={false}
                        type="info"
                        message="Your app uses non-exempt encryption. You’re required to upload export compliance documentation in the Encryption section of App Store Connect. Once your documentation is approved, Apple will provide you with a key value that you can enter in Xcode to avoid needing to set export compliance information on each build uploaded to TestFlight."
                    />
                )}
                {/* <FormItem
                    label={
                        <Space>
                            <Text>Reset iOS summary rating</Text>
                            <Tooltip title="Reset your app’s rating with this new version. Your App Store page will display a message about your rating being reset until enough customers have rated the new version and a new summary rating appears.">
                                <InfoCircleOutlined style={{ color: runwayColors.info }} />
                            </Tooltip>
                        </Space>
                    }
                    name="resetSummaryRating"
                    rules={[{ required: true }]}
                >
                    <Radio.Group>
                        <Radio value={false}>Keep existing rating</Radio>
                        <Radio value={true}>Reset rating when this version is released</Radio>
                    </Radio.Group>
                </FormItem> */}
                <Form layout="vertical">
                    <FormItem
                        label={
                            <Space>
                                <Text>What type of encryption algorithms does your app implement?</Text>
                                <Tooltip title="To bypass setting up export compliance in App Store Connect, you can specify your use of encryption directly in the information property list (Info.plist) in your Xcode project. If you need to provide documentation, Apple will provide you with a key value to add to the Info.plist.">
                                    <InfoCircleOutlined style={{ color: runwayColors.info }} />
                                </Tooltip>
                            </Space>
                        }
                        messageVariables={{ label: 'Export compliance information' }}
                        colon={false}
                    >
                        <Radio.Group
                            onChange={(e) =>
                                handleInitialEncryptionSelection(e.target.value as InitialEncryptionOptions)
                            }
                        >
                            <Space direction="vertical" size={12}>
                                <Radio value={InitialEncryptionOptions.ProprietaryAlgorithms}>
                                    Encryption algorithms that are proprietary or not accepted as standard by
                                    international standard bodies (IEEE, IETF, ITU, etc.)
                                </Radio>
                                <Radio value={InitialEncryptionOptions.StandardAlgorithms}>
                                    Standard encryption algorithms instead of, or in addition to, using or accessing the
                                    encryption within Apple’s operating system
                                </Radio>
                                <Radio value={InitialEncryptionOptions.BothAlgorithms}>
                                    Both algorithms mentioned above
                                </Radio>
                                <Radio value={InitialEncryptionOptions.NoAlgorithms}>
                                    None of the algorithms mentioned above
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </FormItem>
                    <FormItem
                        label={<Text>Is your app going to be available on the App Store in France?</Text>}
                        messageVariables={{ label: 'Exempt encryption selection' }}
                        hidden={!requiresAdditionalQuestion}
                    >
                        <Radio.Group onChange={(e) => handleAdditionalQuestion(e.target.value as boolean)}>
                            <Radio value={true}>Yes</Radio>
                            <Radio value={false}>No</Radio>
                        </Radio.Group>
                    </FormItem>
                    <FormItem
                        label={<Text>Apply default encryption settings to new builds?</Text>}
                        name="shouldApplyDefaultEncryption"
                        messageVariables={{ label: 'shouldApplyDefaultEncryption' }}
                        extra="Runway will apply your default Export Compliance Information answers as defined here to new builds as they are uploaded to App Store Connect and TestFlight if needed."
                    >
                        <Switch checked={shouldApplyDefaultEncryption} onChange={handleApplyDefaultEncryption} />
                    </FormItem>
                </Form>
                <Divider style={{ marginTop: 0, marginBottom: 12 }} />
                <Form form={form} layout="vertical" onFinish={onSaveChanges}>
                    <FormItem
                        label="Release method"
                        name="releaseMethod"
                        extra="Runway will set your default release method on every new release. You can still manually change the release method for a specific release."
                        rules={[{ required: true }]}
                        required
                    >
                        <Radio.Group>
                            <Radio
                                value={ReleaseMethod.Manual}
                                style={{ whiteSpace: 'normal', marginBottom: 12, display: 'flex' }}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Text style={{ lineHeight: 1, marginBottom: 4 }}>Manually release updates</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Choose this release method if you plan to use Runway&apos;s automated release
                                        feature.
                                    </Text>
                                </div>
                            </Radio>
                            <Radio value={ReleaseMethod.AutomaticOnApproval} style={{ display: 'flex' }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Text style={{ lineHeight: 1, marginBottom: 4 }}>
                                        Automatically release updates
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Your app will be released as soon as it’s approved.
                                    </Text>
                                </div>
                            </Radio>
                            {/* <Radio value={ReleaseMethod.AutomaticOnDate}>
                                Automatically release updates after app review, no earlier than:
                            </Radio> */}
                        </Radio.Group>
                    </FormItem>
                    <FormItem
                        label="Phased release"
                        name="phasedRelease"
                        extra="Runway will set your default phased release selection on every new release. You can still manually change the phased release selection for a specific release."
                        rules={[{ required: true }]}
                        required
                    >
                        <Radio.Group>
                            <Space direction="vertical">
                                <Radio value={false}>Release updates to all users immediately</Radio>
                                <Radio value={true}>Release updates over 7-day period using phased release</Radio>
                            </Space>
                        </Radio.Group>
                    </FormItem>
                </Form>
            </div>
        </Modal>
    );
};

export default EditAppleAppReleaseDefaults;
